import { isKelly } from '../components/KellyOnly';
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import AppShell, { useIsPhone } from '../components/AppShell';
import { LogOut, Users, FileText, Bell, CheckSquare, ListTodo } from 'lucide-react';
import { getCurrentThemeIsDark, getLogoBooleanTheme } from '../utils/themeUtils';
import AdminDashboard from '../components/AdminDashboard';
import AgentDashboard from '../components/AgentDashboard';
import BuyerDashboard from '../components/BuyerDashboard';
import ClosingConciergeDashboard from '../components/ClosingConciergeDashboard';
import LocalAssistantDashboard from '../components/LocalAssistantDashboard';
import MarketingManagerDashboard from '../components/MarketingManagerDashboard';
import UpcomingTasksWidget from '../components/UpcomingTasksWidget';
import { GOLD_GRADIENT } from '../styles/gold';
import { isAgentLike } from '../config/roleAccess';

export default function Dashboard() {
  const { user, userRole, displayName, profile, logout } = useAuth();
  const initials = (displayName || user?.email || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const navigate = useNavigate();
  const [adminView, setAdminView] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('admin') === '1'; } catch (e) { return false; }
  });
  const location = useLocation();
  const isPhone = useIsPhone();
  React.useEffect(() => { setAdminView(new URLSearchParams(location.search).get('admin') === '1'); }, [location.search]);
  // Dark theme for dashboard (black background)
  // The dashboard header is black, so always use the white Resi logo.
  const logoSrc = '/resi-logo-white.png';

  const agentUser = { firstName: displayName ? displayName.split(' ')[0] : 'there' };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Admins and agents share the side menu. Admins also get the Admin Dashboard button.
  if (userRole === 'Admin' || isAgentLike(userRole)) {
    const isAdmin = userRole === 'Admin';
    const showAdmin = isAdmin && adminView;
    {
      return (
        <AppShell adminActive={showAdmin}>
          <div style={{ backgroundColor: '#ffffff', minHeight: 'calc(100vh - 60px)' }}>
            {showAdmin ? <AdminDashboard /> : <AgentDashboard currentUser={agentUser} />}
          </div>
        </AppShell>
      );
    }
    return (
      <div style={styles.adminShell}>
        <aside style={styles.sideNav}>
          <img src="/resi-logo-white.png" alt="Resi" style={{ height: 40, width: 'auto', maxWidth: 160, marginBottom: 20, alignSelf: 'flex-start' }} />
          <div style={styles.sideNavLabel}>Menu</div>
          <button
            onClick={() => setAdminView(false)}
            style={{ ...styles.sideNavButton, ...(!showAdmin ? styles.sideNavButtonActive : {}) }}
          >
            Agent Dashboard
          </button>
          <button onClick={() => navigate('/transactions')} style={styles.sideNavButton}>
            My Transactions
          </button>
          <button onClick={() => navigate('/tasks')} style={styles.sideNavButton}>
            My Tasks
          </button>
          {isKelly(user?.uid) && <button onClick={() => navigate('/website')} style={styles.sideNavButton}>
            My Website
          </button>}
          <button onClick={() => navigate('/leads')} style={styles.sideNavButton}>
            My Leads
          </button>
          <button onClick={() => navigate('/open-houses')} style={styles.sideNavButton}>
            Open Houses
          </button>
          <button onClick={() => navigate('/tools')} style={styles.sideNavButton}>
            Tools
          </button>
          <button onClick={() => navigate('/training')} style={styles.sideNavButton}>
            Training
          </button>
          <button onClick={() => navigate('/calendar')} style={styles.sideNavButton}>
            Calendar
          </button>
          <button onClick={() => navigate('/profile')} style={styles.sideNavButton}>
            My Profile
          </button>
          {isAdmin && (
            <button
              onClick={() => setAdminView(true)}
              style={{ ...styles.sideNavButton, ...(showAdmin ? styles.sideNavButtonActive : {}) }}
            >
              Admin Dashboard
            </button>
          )}
          <button onClick={handleSignOut} style={styles.sideNavSignOut}>
            <LogOut size={16} />
            Sign Out
          </button>
        </aside>
        <div style={styles.adminContent}>
          {showAdmin ? <AdminDashboard /> : <AgentDashboard currentUser={agentUser} />}
        </div>
      </div>
    );
  }
  if (userRole === 'Buyer') {
    return <BuyerDashboard />;
  }
  // Team roles get the same menu as everyone else (side menu on desktop, hamburger on phones).
  const inShell = (el) => <AppShell><div style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>{el}</div></AppShell>;
  if (userRole === 'Closing Concierge') {
    return inShell(<ClosingConciergeDashboard currentUser={agentUser} />);
  }
  if (userRole === 'Local Assistant') {
    return inShell(<LocalAssistantDashboard currentUser={agentUser} />);
  }
  if (userRole === 'Marketing Manager') {
    return inShell(<MarketingManagerDashboard currentUser={agentUser} />);
  }

  // For other roles or no role assigned, show generic dashboard
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <AppShell>
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <img src={logoSrc} alt="Resi" style={styles.logo} />
          <div style={styles.userInfo}>
            <span style={styles.userEmail}>{user?.email}</span>
            <span style={styles.roleBadge}>{userRole}</span>
          </div>
        </div>
        <button onClick={handleLogout} style={styles.logoutButton}>
          <LogOut size={18} />
          Sign Out
        </button>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        <div style={{ ...styles.welcomeSection, display: 'flex', alignItems: 'center', gap: '18px' }}>
          {profile?.photoUrl ? (
            <img
              src={profile.photoUrl}
              alt={displayName || 'Profile'}
              style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #D4AF37', flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 72, height: 72, borderRadius: '50%', border: '2px solid #D4AF37', background: '#1a1a1a', color: '#D4AF37', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 22, flexShrink: 0 }}>
              {initials}
            </div>
          )}
          <div>
            <h2 style={styles.greeting}>Welcome back!</h2>
            <p style={styles.subtitle}>
              {displayName || user?.email}
            </p>
          </div>
        </div>

        {/* Upcoming Tasks Widget */}
        <div style={styles.upcomingTasksSection}>
          <UpcomingTasksWidget />
        </div>

        {/* Navigation Grid */}
        <div style={styles.grid}>
          {/* Transactions */}
          <div style={styles.card}>
            <div style={styles.cardIcon}>
              <FileText size={32} />
            </div>
            <h3 style={styles.cardTitle}>Transactions</h3>
            <p style={styles.cardDescription}>View and manage all transactions</p>
            <div style={styles.buttonGroup}>
              <button
                onClick={() => navigate('/transactions')}
                style={styles.cardButton}
              >
                View Transactions
              </button>
              <button
                onClick={() => navigate('/transactions/new')}
                style={{...styles.cardButton, backgroundColor: '#FFFFFF', color: '#000000'}}
              >
                Create New
              </button>
            </div>
          </div>

          {/* Tasks */}
          <div style={styles.card}>
            <div style={styles.cardIcon}>
              <ListTodo size={32} />
            </div>
            <h3 style={styles.cardTitle}>Tasks</h3>
            <p style={styles.cardDescription}>Track deadline-based tasks</p>
            <button
              onClick={() => navigate('/tasks')}
              style={styles.cardButton}
            >
              View Tasks
            </button>
          </div>

          {/* Checklists */}
          <div style={styles.card}>
            <div style={styles.cardIcon}>
              <CheckSquare size={32} />
            </div>
            <h3 style={styles.cardTitle}>Checklists</h3>
            <p style={styles.cardDescription}>Track transaction checklists</p>
            <button style={styles.cardButton} disabled>
              Coming Soon
            </button>
          </div>

          {/* Notifications */}
          <div style={styles.card}>
            <div style={styles.cardIcon}>
              <Bell size={32} />
            </div>
            <h3 style={styles.cardTitle}>Notifications</h3>
            <p style={styles.cardDescription}>View task and closing reminders</p>
            <button
              onClick={() => navigate('/notifications')}
              style={styles.cardButton}
            >
              View Notifications
            </button>
          </div>

          {/* Admin Panel - Only for Admins */}
          {userRole === 'Admin' && (
            <div style={styles.card}>
              <div style={styles.cardIcon}>
                <Users size={32} />
              </div>
              <h3 style={styles.cardTitle}>User Management</h3>
              <p style={styles.cardDescription}>Add and manage team members</p>
              <button
                onClick={() => navigate('/admin')}
                style={styles.cardButton}
              >
                Manage Users
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </AppShell>
  );
}

const styles = {
  adminShell: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#000000'
  },
  sideNav: {
    width: '220px',
    flexShrink: 0,
    backgroundColor: '#1a1a1a',
    borderRight: '2px solid #D4AF37',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
    height: '100vh',
    boxSizing: 'border-box'
  },
  sideNavLabel: {
    fontSize: '12px',
    color: '#D4AF37',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px',
    fontWeight: '600'
  },
  sideNavButton: {
    width: '100%',
    textAlign: 'left',
    padding: '12px 14px',
    backgroundColor: 'transparent',
    color: '#FFFFFF',
    border: '1px solid #333333',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  sideNavButtonActive: {
    background: GOLD_GRADIENT,
    color: '#000000',
    borderColor: '#D4AF37'
  },
  sideNavSignOut: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: 'transparent',
    color: '#999999',
    border: '1px solid #333333',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  adminContent: {
    flex: 1,
    minWidth: 0,
    margin: '16px',
    marginLeft: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    minHeight: 'calc(100vh - 32px)',
    overflow: 'hidden'
  },
  container: {
    minHeight: '100vh',
    backgroundColor: '#000000',
    color: '#FFFFFF',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  header: {
    backgroundColor: '#1a1a1a',
    borderBottom: '2px solid #D4AF37',
    padding: '20px 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
  logo: {
    height: '40px',
    width: 'auto',
    maxWidth: '200px'
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  userEmail: {
    fontSize: '14px',
    color: '#999999'
  },
  roleBadge: {
    fontSize: '12px',
    backgroundColor: '#D4AF37',
    color: '#000000',
    padding: '4px 12px',
    borderRadius: '4px',
    fontWeight: '600',
    width: 'fit-content',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  logoutButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#D4AF37',
    color: '#000000',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  mainContent: {
    padding: '40px'
  },
  welcomeSection: {
    marginBottom: '40px'
  },
  greeting: {
    fontSize: '32px',
    fontWeight: 'bold',
    margin: '0 0 8px 0',
    color: '#FFFFFF',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  subtitle: {
    fontSize: '16px',
    color: '#D4AF37',
    margin: '0',
    letterSpacing: '0.5px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  upcomingTasksSection: {
    marginBottom: '40px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px'
  },
  card: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: '8px',
    padding: '24px',
    transition: 'all 0.3s ease'
  },
  cardIcon: {
    color: '#D4AF37',
    marginBottom: '16px',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0a0a0a',
    borderRadius: '8px'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#FFFFFF',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#999999',
    margin: '0 0 16px 0',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  cardButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#D4AF37',
    color: '#000000',
    border: 'none',
    borderRadius: '4px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    flexDirection: 'column'
  }
};
